import { Team } from '@/hooks/useTeams';
import {
  useCopilotAction,
  useCopilotChat,
  useCopilotReadable,
} from '@copilotkit/react-core';
import { Role, TextMessage } from '@copilotkit/runtime-client-gql';
import { RefObject, useEffect } from 'react';
import { SubmitHandler, UseFormSetValue } from 'react-hook-form';
import * as yup from 'yup';

export type TeamOnboardingQuestions = {
  [K in keyof Pick<
    Team,
    | 'description'
    | 'industry'
    | 'audience'
    | 'tone_and_style'
    | 'posting_frequency'
    | 'brand_voice'
    | 'website_url'
  >]-?: NonNullable<Team[K]>;
};

const agentName = 'Sam';

const standardizedQuestions = [
  // Description
  {
    question:
      'Tell us about your business or brand. What do you do, and what makes you unique?',
    relatedFields: ['description'],
  },
  {
    question:
      'What are the key goals of your social media presence? (e.g., brand awareness, lead generation, sales, community building)',
    relatedFields: ['description'],
  },

  // Industry
  {
    question: 'Which industry or niche does your business operate in?',
    relatedFields: ['industry'],
  },
  {
    question:
      'Are there any subcategories or specific focus areas within your industry?',
    relatedFields: ['industry'],
  },

  // Target Audience
  {
    question:
      'Who is your ideal audience? Please describe their demographics (age, location, gender)',
    relatedFields: ['audience'],
  },
  {
    question: 'What are their interests and behaviors?',
    relatedFields: ['audience'],
  },
  {
    question: 'What challenges or pain points do you solve for them?',
    relatedFields: ['audience'],
  },
  {
    question: "Are there specific customer personas you'd like us to focus on?",
    relatedFields: ['audience'],
  },

  // Tone and Style
  {
    question:
      'How would you describe the tone you want for your posts? (e.g., professional, casual, inspiring, witty)',
    relatedFields: ['tone_and_style'],
  },
  {
    question:
      "Are there any specific emotions you'd like your posts to evoke in your audience?",
    relatedFields: ['tone_and_style'],
  },

  // Brand Voice
  {
    question:
      'What three words best describe your brand voice? (e.g., bold, trustworthy, playful)',
    relatedFields: ['brand_voice'],
  },
  {
    question:
      'Are there phrases, slogans, or taglines that reflect your brand?',
    relatedFields: ['brand_voice'],
  },
  {
    question:
      "Do you have specific do's and don'ts for how your brand should communicate? (e.g., avoid jargon, always be inclusive)",
    relatedFields: ['brand_voice'],
  },
  {
    question:
      'Help me understand your brand voice, by providing me with the URL of your company website?',
    relatedFields: ['website_url'],
  },
];

const directQuestions: Record<
  keyof Pick<TeamOnboardingQuestions, 'posting_frequency'>,
  string
> = {
  posting_frequency: 'How often do you want to post on social media per week?',
};

export const useTeamOnboardingAgent = ({
  setValue,
  formValues,
  inputRef,
  validationSchema,
  handleSubmit,
}: {
  setValue: UseFormSetValue<TeamOnboardingQuestions>;
  formValues: any;
  inputRef: RefObject<HTMLTextAreaElement>;
  validationSchema: yup.ObjectSchema<any>;
  handleSubmit: SubmitHandler<TeamOnboardingQuestions>;
}) => {
  const { visibleMessages, appendMessage, setMessages, isLoading } =
    useCopilotChat({
      id: 'team-onboarding',
      makeSystemMessage:
        () => `You are ${agentName}, a strategic social media consultant who helps brands and agencies define their social media presence with clarity and purpose. Your approach is professional yet conversational - you understand the business context while keeping discussions engaging and insightful.

Core Traits:
- You speak the language of brands and marketing professionals
- You balance strategic thinking with creative exploration
- You help teams articulate their unique market position
- You ask focused questions that drive meaningful insights
- You recognize industry-specific nuances and challenges
- You help distill complex brand identities into clear descriptions

Important Guidelines:
- Ask only ONE question at a time
- Wait for the response before moving to the next question
- Keep follow-up questions natural and conversational
- Don't list multiple questions in a single message
- Build on previous answers to make the conversation flow naturally

You should gather information about:
- Brand description
- Industry
- Target audience
- Tone and style
- Brand voice
- Weekly posting frequency
- Website URL

Remember: Keep each interaction focused on a single question, making the conversation easy to follow and engage with.`,
    });

  const getMissingFields = (
    currentValues: Partial<TeamOnboardingQuestions>,
  ) => {
    const requiredFields = Object.keys(
      directQuestions,
    ) as (keyof typeof directQuestions)[];

    return requiredFields.filter((field) => !currentValues[field]);
  };

  const getNextConversationalQuestion = (
    currentValues: Partial<TeamOnboardingQuestions>,
  ) => {
    return standardizedQuestions.find((q) =>
      q.relatedFields.some(
        (field) => !currentValues[field as keyof TeamOnboardingQuestions],
      ),
    );
  };

  useCopilotReadable({
    description: 'Current team information',
    value: formValues,
  });

  // @ts-expect-error - CopilotKit requires "moduleResolution": "bundler" in tsconfig.json,
  // but project uses "node". This causes type conflicts that can be safely ignored
  // as the runtime behavior is correct despite the type mismatch.
  useCopilotAction({
    name: 'CollectTeamInfo',
    description: 'Collect team information',
    parameters: [
      { name: 'description', type: 'string' },
      { name: 'tone_and_style', type: 'string' },
      { name: 'industry', type: 'string' },
      { name: 'audience', type: 'string' },
      { name: 'posting_frequency', type: 'number' },
      { name: 'brand_voice', type: 'string' },
      { name: 'website_url', type: 'string' },
    ],
    handler: async (parameters: TeamOnboardingQuestions) => {
      try {
        await validationSchema.validate(parameters);
        Object.keys(parameters).forEach((key) => {
          setValue(
            key as keyof TeamOnboardingQuestions,
            parameters[key as keyof TeamOnboardingQuestions],
          );
        });

        console.log('parameters', parameters);

        await handleSubmit(parameters);

        setMessages([]);
      } catch (error) {
        if (error instanceof yup.ValidationError) {
          const missingFields = getMissingFields(formValues);
          const nextConversationalQ = getNextConversationalQuestion(formValues);

          if (nextConversationalQ) {
            appendMessage(
              new TextMessage({
                content: `That's interesting! ${nextConversationalQ.question}`,
                role: Role.Assistant,
              }),
            );
          } else if (missingFields.length > 0) {
            const nextField = missingFields[0];
            appendMessage(
              new TextMessage({
                content: `Just a few more details needed! ${directQuestions[nextField]}`,
                role: Role.Assistant,
              }),
            );
          }

          return;
        }
        console.error('Validation error:', error);
      }
    },
  });

  useEffect(() => {
    setMessages([
      new TextMessage({
        content: `## 👋 Hey there! I'm ${agentName} 👋
I'm here to help shape your social media strategy with clarity and purpose. Let's have a strategic conversation about your brand and the unique value you bring to your audience.

To start, if your brand were introducing itself at a networking event, what would be its go-to one-liner?`,
        role: Role.System,
      }),
    ]);

    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [inputRef, setMessages]);

  return { visibleMessages, appendMessage, isLoading };
};
